import React from "react";
import { logDebug } from "@/lib/log";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Full-screen takeover on an audience phone the moment Dan puts THEIR
 * word on the big screen. Purely a payoff moment — it auto-dismisses.
 */
const SPARKS = Array.from({ length: 14 }, (_, i) => i);

export function OnStageBurst({ words, mode, onDone, holdMs = 9000 }) {
  React.useEffect(() => {
    if (!words) return undefined;
    try {
      if (navigator.vibrate) navigator.vibrate([0, 60, 60, 120]);
    } catch (err) {
      // No haptics on this device — not worth interrupting the show.
      logDebug("vibrate unavailable", err);
    }
    const t = window.setTimeout(onDone, holdMs);
    return () => window.clearTimeout(t);
  }, [words, onDone, holdMs]);

  return (
    <AnimatePresence>
      {words ? (
        <motion.div
          className="fixed inset-0 z-[80] grid place-items-center px-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{ backgroundColor: "rgba(8,4,25,0.94)", backdropFilter: "blur(6px)" }}
          onClick={onDone}
          data-testid="live-audience-onstage"
        >
          {SPARKS.map((i) => (
            <motion.span
              key={i}
              className="absolute rounded-full"
              initial={{ opacity: 0, scale: 0.2, x: 0, y: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0.2, 1, 0.6],
                x: `${(Math.random() - 0.5) * 90}vw`,
                y: `${(Math.random() - 0.5) * 80}vh`,
              }}
              transition={{ duration: 1.6 + Math.random(), delay: i * 0.05, ease: "easeOut" }}
              style={{
                width: 10,
                height: 10,
                backgroundColor: ["#F2D589", "#A774FF", "#FCA07A", "#B79CE8"][i % 4],
                boxShadow: "0 0 24px currentColor",
              }}
            />
          ))}

          <div className="relative">
            <motion.p
              className="text-[11px] uppercase tracking-[0.4em]"
              style={{ color: "#F2D589" }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
            >
              You&apos;re on the big screen
            </motion.p>
            <motion.h2
              className="mt-5"
              dir={mode === "hebrew" ? "rtl" : "ltr"}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: [0.7, 1.12, 1] }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{
                fontFamily: mode === "hebrew"
                  ? "'Frank Ruhl Libre', serif"
                  : "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontSize: "clamp(44px, 13vw, 96px)",
                lineHeight: 1.05,
                color: "#F5EFE3",
                textShadow: "0 0 60px rgba(232,196,138,0.35)",
              }}
              data-testid="live-audience-onstage-words"
            >
              {words}
            </motion.h2>
            <motion.p
              className="mt-6 text-base opacity-70"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.5 }}
            >
              Look up — that&apos;s your melody playing.
            </motion.p>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default OnStageBurst;
